import { Button, Group, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import getErrorMessage from '../../helpers/getErrorMessage';
import useFetch from '../../helpers/useFetch';
import ConfirmationModal from '../Modals/ConfirmationModal';
import FacebookCatalogCleanupResult, { describeCatalogCleanupResult } from './FacebookCatalogCleanupResult';

const POLL_INTERVAL_MS = 5000;
const POLL_TIMEOUT_MS = 10 * 60 * 1000;

const formatCount = (value) => Number(value).toLocaleString();

const buildConfirmationMessage = (uploadSummary, facebookAccount) => {
  const { catalogProductCount, mainFeedProductCount } = uploadSummary || {};
  const cleanupSummary = facebookAccount.lastCatalogCleanupSummary || {};
  const previewSentence =
    facebookAccount.lastCatalogCleanupVerdict === 'ok' && cleanupSummary.dryRun && cleanupSummary.staleCount != null
      ? `The last preview found ${formatCount(cleanupSummary.staleCount)} stale items. `
      : '';
  const countsSentence =
    catalogProductCount != null && mainFeedProductCount != null
      ? `Products in catalog: ${formatCount(catalogProductCount)}, owned by the Releva feed: ${formatCount(mainFeedProductCount)}. `
      : '';
  return `${previewSentence}${countsSentence}Items not owned by any Releva feed will be deleted from the Meta catalog, then a catalog sync will run.`;
};

const showCleanupOutcome = (refreshedAccount) => {
  const verdict = refreshedAccount.lastCatalogCleanupVerdict;
  const { title } = describeCatalogCleanupResult(verdict, refreshedAccount.lastCatalogCleanupSummary || {});
  notifications.show({ color: verdict === 'ok' ? 'success' : 'danger', title: 'Catalog cleanup', message: title });
};

const FacebookRemoveStaleCatalogItemsButton = ({ facebookAccount, onFacebookAccountChange }) => {
  const fetch = useFetch();
  const { accountId, domainId } = useParams();
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
  const [activeRun, setActiveRun] = useState(null);
  const [timedOut, setTimedOut] = useState(false);
  const inFlightRef = useRef(false);
  const pollRef = useRef(null);
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearInterval(pollRef.current?.intervalId);
      clearTimeout(pollRef.current?.timeoutId);
      pollRef.current = null;
    };
  }, []);

  const finishRun = () => {
    clearInterval(pollRef.current?.intervalId);
    clearTimeout(pollRef.current?.timeoutId);
    pollRef.current = null;
    inFlightRef.current = false;
    setActiveRun(null);
  };

  const pollForCleanupResult = (previousCleanupAt) => {
    const intervalId = setInterval(() => {
      fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/facebook/account`).then(
        ({ facebookAccount: refreshedAccount }) => {
          const cleanupAt = refreshedAccount?.lastCatalogCleanupAt;
          if (pollRef.current?.intervalId !== intervalId || !cleanupAt || cleanupAt === previousCleanupAt) {
            return;
          }
          finishRun();
          onFacebookAccountChange(refreshedAccount);
          showCleanupOutcome(refreshedAccount);
        },
        () => null
      );
    }, POLL_INTERVAL_MS);
    const timeoutId = setTimeout(() => {
      finishRun();
      setTimedOut(true);
      notifications.show({
        color: 'info',
        message: 'The cleanup is still running. Reload the page later to see its result.',
      });
    }, POLL_TIMEOUT_MS);
    pollRef.current = { intervalId, timeoutId };
  };

  const startCleanup = async (dryRun) => {
    if (inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;
    setTimedOut(false);
    setActiveRun(dryRun ? 'preview' : 'remove');
    try {
      const previousCleanupAt = await fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/facebook/account`)
        .then((response) => response.facebookAccount.lastCatalogCleanupAt)
        .catch(() => facebookAccount.lastCatalogCleanupAt);
      await fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/facebook/removeStaleCatalogItems`, {
        method: 'POST',
        body: JSON.stringify({ dryRun }),
      });
      notifications.show({ color: 'success', title: 'Catalog cleanup', message: 'Cleanup scheduled' });
      if (mountedRef.current) {
        pollForCleanupResult(previousCleanupAt);
      }
    } catch (err) {
      notifications.show({
        color: 'danger',
        message: getErrorMessage(err, 'Could not schedule the catalog cleanup. Please try again.'),
      });
      inFlightRef.current = false;
      setActiveRun(null);
    } finally {
      setConfirmRemoveOpen(false);
    }
  };

  const unavailable = !!facebookAccount.useExternalCatalog || !facebookAccount.feedId;
  const disabled = unavailable || activeRun !== null;

  const keepConfirmSubmitOutOfSettingsForm = (event) => event.stopPropagation();

  return (
    <Stack gap="xs" onSubmit={keepConfirmSubmitOutOfSettingsForm}>
      <Text fw={500}>Stale catalog items</Text>
      <Text size="sm" c="dimmed">
        Finds items in the Meta catalog that no Releva feed owns any more, left over from earlier catalog syncs, and
        removes them. Preview only shows what would be removed and deletes nothing.
      </Text>
      <Group gap="xs">
        <Button
          variant="light"
          disabled={disabled}
          loading={activeRun === 'preview'}
          onClick={() => startCleanup(true)}
        >
          Preview stale items
        </Button>
        <Button
          color="danger"
          disabled={disabled}
          loading={activeRun === 'remove'}
          onClick={() => setConfirmRemoveOpen(true)}
        >
          Remove stale items
        </Button>
        <ConfirmationModal
          show={confirmRemoveOpen}
          close={() => setConfirmRemoveOpen(false)}
          confirm={() => startCleanup(false)}
          loading={activeRun === 'remove'}
          message={buildConfirmationMessage(facebookAccount.lastFeedUploadSummary, facebookAccount)}
        />
      </Group>
      <FacebookCatalogCleanupResult facebookAccount={facebookAccount} activeRun={activeRun} stillRunning={timedOut} />
    </Stack>
  );
};

export default FacebookRemoveStaleCatalogItemsButton;
